import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type {
  EvenementFile,
  Observation,
  Patrouille,
  PhotoLocale,
  SecteurZonage,
  Signalement,
  TotauxSaison,
} from '../domaine/types'

/*
  Stockage local — IndexedDB via `idb`.

  Pas de localStorage : il est synchrone (il bloque le fil principal, donc le
  chrono et les appuis sur « +1 ») et plafonné à ~5 Mo, ce qui ne tient pas
  face aux photos. L'unique exception est la copie du thème lue par le script
  anti-flash de index.html, qui doit être synchrone par nature.

  Aucun magasin ne contient de coordonnée géographique — cf. domaine/types.ts.
*/

/** Réglages persistants. Typés un par un pour éviter les `any` disséminés. */
export interface Reglages {
  device_token: string
  saison_id: number
  saison_libelle: string
  /** Prénom libre et facultatif. Jamais de nom de famille. */
  prenom: string
  theme: 'normal' | 'rouge'
  /** 30 à 100. Intensité du mode nuit. */
  luminosite_nuit: number
  zonage_version: number
  /** ISO 8601 du dernier envoi réussi. Affiché dans le bandeau de file. */
  dernier_envoi_ok: string
  /** Id de la patrouille en cours, absent s'il n'y en a pas. */
  patrouille_active: string
}
export type CleReglage = keyof Reglages
type ValeurReglage = Reglages[CleReglage]

export interface BaseKwata extends DBSchema {
  reglages: { key: string; value: ValeurReglage }
  zonage: { key: string; value: SecteurZonage }
  patrouilles: { key: string; value: Patrouille }
  observations: {
    key: string
    value: Observation
    indexes: { 'par-patrouille': string }
  }
  signalements: { key: string; value: Signalement }
  photos: { key: string; value: PhotoLocale }
  file: {
    key: string
    value: EvenementFile
    indexes: { 'par-rang': number; 'par-etat': string }
  }
  cache_saison: { key: string; value: TotauxSaison & { recu_le: string } }
}

const NOM = 'kwata'
const VERSION = 1

let instance: Promise<IDBPDatabase<BaseKwata>> | null = null

export function base(): Promise<IDBPDatabase<BaseKwata>> {
  if (!instance) {
    instance = openDB<BaseKwata>(NOM, VERSION, {
      upgrade(db, ancienne) {
        // Les migrations sont cumulatives : chaque bloc ne s'exécute qu'une fois,
        // en montant depuis la version installée sur l'appareil.
        if (ancienne < 1) {
          db.createObjectStore('reglages')
          db.createObjectStore('zonage', { keyPath: 'code' })
          db.createObjectStore('patrouilles', { keyPath: 'id' })

          const obs = db.createObjectStore('observations', { keyPath: 'id' })
          obs.createIndex('par-patrouille', 'patrouille_id')

          db.createObjectStore('signalements', { keyPath: 'id' })
          db.createObjectStore('photos', { keyPath: 'id' })

          const file = db.createObjectStore('file', { keyPath: 'id' })
          // Les uuid sont aléatoires : sans index de rang, l'ordre d'envoi
          // serait celui des clés, donc arbitraire. On veut du FIFO.
          file.createIndex('par-rang', 'rang')
          file.createIndex('par-etat', 'etat')

          db.createObjectStore('cache_saison')
        }
      },

      blocked() {
        console.warn('[kwata] Un autre onglet retient une ancienne version de la base.')
      },
      blocking() {
        // Une nouvelle version veut s'installer : on libère la connexion
        // plutôt que de bloquer la mise à jour du service worker.
        void base().then((db) => db.close())
        instance = null
      },
    })
  }
  return instance
}

/* ── Réglages ─────────────────────────────────────────────────────── */

export async function lireReglage<K extends CleReglage>(
  cle: K,
): Promise<Reglages[K] | undefined> {
  const db = await base()
  return (await db.get('reglages', cle)) as Reglages[K] | undefined
}

export async function ecrireReglage<K extends CleReglage>(
  cle: K,
  valeur: Reglages[K],
): Promise<void> {
  const db = await base()
  await db.put('reglages', valeur, cle)
}

export async function effacerReglage(cle: CleReglage): Promise<void> {
  const db = await base()
  await db.delete('reglages', cle)
}

/* ── Zonage ───────────────────────────────────────────────────────── */

export async function remplacerZonage(
  secteurs: SecteurZonage[],
  version: number,
): Promise<void> {
  const db = await base()
  const tx = db.transaction(['zonage', 'reglages'], 'readwrite')
  await tx.objectStore('zonage').clear()
  for (const s of secteurs) {
    await tx.objectStore('zonage').put(s)
  }
  await tx.objectStore('reglages').put(version, 'zonage_version')
  await tx.done
}

export async function lireZonage(): Promise<SecteurZonage[]> {
  const db = await base()
  return db.getAll('zonage')
}

/* ── Utilitaire de test ───────────────────────────────────────────── */

/** Ferme la connexion. Utilisé entre deux tests pour repartir propre. */
export function fermerBase(): void {
  if (instance) {
    void instance.then((db) => db.close())
    instance = null
  }
}
